import React from 'react';
import PropTypes from 'prop-types';
import {TabList as ReactTabList} from 'react-tabs';
import styled, {css} from 'styled-components';
import breakpoint from 'styled-components-breakpoint';
import {px, pt, pb} from 'styled-components-spacing';
import {Box, colorValues, validColorValues, pickColor} from '@nib/layout';
import {STACKED_BREAKPOINT, BORDER_WIDTH, BLACK_10, BLACK_20, WHITE_20} from './config';

const validVariationValues = ['default', 'container'] as const;
type variationValues = typeof validVariationValues[number];

export interface TabListProps {
  background?: colorValues;
  center?: boolean;
  fill?: boolean;
  invert?: boolean;
  variation?: variationValues;
  children?: React.ReactNode;
  [key: string]: unknown; // other props
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Wrapper = styled(({background, color, variation, ...rest}) => <Box background={background} color={color} {...rest} />)`
  ${breakpoint(STACKED_BREAKPOINT)`
    overflow-x: auto;
    overflow-y: visible;

    /* Scrolling shadows adapated from http://lea.verou.me/2012/04/background-attachment-local/ */
    /* Codepen link: https://codepen.io/laurie/pen/GBqGWV?editors=0100 */
    background:
      /* Shadow covers */
      linear-gradient(to right, ${(props: {background: colorValues}) => pickColor(props.background)} 30%, transparent),
      linear-gradient(to left, ${(props: {background: colorValues}) => pickColor(props.background)} 70%, transparent) 100% 100%,
      
      /* Shadows */
      radial-gradient(50% 0, farthest-side, ${BLACK_20}, transparent),
      radial-gradient(50% 100%, farthest-side, ${BLACK_20}, transparent) 100% 100%;
    
    background:		
      /* Shadow covers */
      linear-gradient(to right, ${(props: {background: colorValues}) => pickColor(props.background)} 30%, transparent),
      linear-gradient(to left, ${(props: {background: colorValues}) => pickColor(props.background)} 70%, transparent) 100% 100%,
    
      /* Shadows */
      radial-gradient(farthest-side at 0 50%, ${BLACK_20}, transparent),
      radial-gradient(farthest-side at 100% 50%, ${BLACK_20}, transparent) 100% 100%;
    
    background-repeat: no-repeat;
    background-color: ${(props: {background: colorValues}) => pickColor(props.background)};
    background-size: 40px 100%, 40px 100%, 14px 100%, 14px 100%;
    
    /* Opera doesn't support this in the shorthand */
    background-attachment: local, local, scroll, scroll;

    ${({variation}: {variation: variationValues}) =>
      variation === 'container' &&
      css`
        ${px(4)};
        ${pt(6)};
        ${pb(0)};
      `}
  `};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const InnerTabList = styled(({center, invert, variation, ...rest}) => <ReactTabList {...rest} />)<TabListProps>`
  margin: 0;
  padding: 0;

  ${({center, variation}) => breakpoint(STACKED_BREAKPOINT)`
    display: inline-flex;
    ${center && 'justify-content: center'};
    min-width: 100%;
    ${
      variation !== 'container' &&
      css`
        border-bottom-width: ${BORDER_WIDTH};
        border-bottom-style: solid;
        border-color: ${({invert}: {invert?: boolean}) => (invert ? WHITE_20 : BLACK_10)};
      `
    }
  `};
`;

const WrappedTabList: React.FC<TabListProps> & {tabsRole: string} = ({children, background, color, center, fill, invert, variation, ...otherProps}) => {
  //eslint-disable-next-line no-undef
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const invariant = require('invariant');
    invariant(!(center && fill), '@nib-components/tabs: TabList cannot have both `center={true}` and `fill={true}`. Choose one.');
  }

  const defaultBackgroundValue = variation === 'container' ? 'lightest' : 'white';

  return (
    <Wrapper background={background ? background : defaultBackgroundValue} color={color} variation={variation}>
      <InnerTabList invert={invert} center={center} variation={variation} {...otherProps}>
        {React.Children.map(children, (child: any) => React.cloneElement(child, {fill, invert, variation}))}
      </InnerTabList>
    </Wrapper>
  );
};

WrappedTabList.propTypes = {
  background: PropTypes.oneOf(validColorValues),
  color: PropTypes.oneOf(validColorValues),
  center: PropTypes.bool,
  fill: PropTypes.bool,
  invert: PropTypes.bool,
  variation: PropTypes.oneOf(validVariationValues),
  children: PropTypes.node
};

WrappedTabList.defaultProps = {
  center: false,
  fill: false,
  invert: false,
  variation: 'default'
};

WrappedTabList.tabsRole = 'TabList';
WrappedTabList.displayName = 'TabList';

export default WrappedTabList;
